import { LeftOutlined, RightOutlined } from "@ant-design/icons"
import { Button } from "components/Button/Button"
import { ShortcutLabel } from "components/ShortcutLabel/ShortcutLabel"
import { Tooltip } from "components/Tooltip/Tooltip"
import {
	ComponentProps,
	KeyboardEventHandler,
	ReactNode,
	useLayoutEffect,
	useState,
} from "react"
import { useHotkeys } from "react-hotkeys-hook"
import { cn } from "styles/cn"

export interface StepperProps extends ComponentProps<"div"> {
	onClickStep?: (index: number) => void
	steps: {
		title?: string
		subtitle?: string
		content: ReactNode
	}[]
}

export type StepperStep = StepperProps["steps"][number]

export function Stepper({
	className,
	steps,
	onClickStep,
	...props
}: StepperProps) {
	const [current, setCurrent] = useState(0)
	const ariaLabelList =
		steps.length > 0
			? `There are ${steps.length} steps available`
			: "No steps available"
	const previousStep = steps[current - 1]
	const nextStep = steps[current + 1]
	const handlePrevious = () => current > 0 && handleClick(current - 1)
	const handleNext = () =>
		current < steps.length - 1 && handleClick(current + 1)
	const handleClick = (index: number) => {
		setCurrent(index)
		onClickStep?.(index)
	}

	useHotkeys("ArrowLeft", handlePrevious)
	useHotkeys("ArrowRight", handleNext)

	useLayoutEffect(() => {
		document.getElementById(stepId(current))?.scrollIntoView({
			behavior: "smooth",
			block: "center",
			inline: "center",
		})
	}, [current])

	return (
		<div
			{...props}
			className={cn(
				"@container flex flex-row justify-center gap-4 p-1",
				className,
			)}
		>
			<div className="hidden @2xl:block">
				<Tooltip title={<ShortcutLabel label="Previous" shortcut="←" />}>
					<Button
						type="text"
						disabled={current === 0 || steps.length === 0}
						icon={<LeftOutlined aria-hidden />}
						aria-label={`Go to ${previousStep ? `step ${previousStep.title ?? previousStep.subtitle}` : "previous step"}`}
						onClick={handlePrevious}
					/>
				</Tooltip>
			</div>

			<ul
				aria-label={ariaLabelList}
				className={cn(
					"scrollbar-hidden flex max-w-sm flex-col overflow-hidden",
					"@2xl:max-w-full @2xl:flex-row",
				)}
			>
				<div className="hidden min-w-lg @2xl:block" />

				{steps.map((step, index) => {
					const title = step.title ?? `Step ${index + 1}`
					const isFirst = index === 0
					const isLast = index === steps.length - 1
					const isVisited = index < current
					const isCurrent = index === current
					const isPreviousCurrent = index - 1 === current
					const onClick = () => {
						handleClick(index)
					}
					const onKeyDown: KeyboardEventHandler<HTMLLIElement> = (e) => {
						if (e.key === "Enter" || e.key === " ") {
							handleClick(index)
						}
					}

					return (
						<li
							id={stepId(index)}
							key={title}
							aria-label={title}
							aria-selected={isCurrent}
							className={cn(
								"focus-outline-transparent flex justify-center gap-4 rounded-2xl hover:cursor-pointer",
								"@2xl:min-w-xl @2xl:flex-col @2xl:items-center @2xl:gap-2",
							)}
							onClick={onClick}
							onKeyDown={onKeyDown}
						>
							<div
								className={cn(
									"flex min-h-full flex-col items-center justify-center",
									"@2xl:min-h-auto @2xl:w-full @2xl:flex-row",
								)}
							>
								<div
									className={cn(
										"h-1 w-0.75 bg-grey-10 @2xl:h-0.75 @2xl:w-full",
										(isVisited || isCurrent) && "bg-green-60",
										isPreviousCurrent && "@max-2xl:bg-green-60",
										isFirst && "invisible",
									)}
								/>
								<div className="py-1 @2xl:px-1">
									<div
										className={cn(
											"size-2.5 rounded-full bg-navy-40",
											(isVisited || isCurrent) && "bg-green-60",
										)}
									/>
								</div>
								<div
									className={cn(
										"h-full w-0.75 bg-grey-10 @2xl:h-0.75 @2xl:w-full",
										isVisited && "bg-green-60",
										isCurrent && "@max-2xl:bg-green-60",
										isLast && "invisible",
									)}
								/>
							</div>

							<div className="flex w-full flex-col items-center gap-3 @2xl:gap-4">
								<h3
									tabIndex={0}
									className={cn(
										"focus-outline pointer-events-none flex w-fit flex-col rounded outline-offset-3!",
										"@max-2xl:place-self-start @2xl:items-center",
									)}
								>
									<p className="text-lg">{isCurrent ? "In progress" : title}</p>
									<p className="text-navy-60">{step.subtitle}</p>
								</h3>

								<div
									className={cn(
										"invisible w-full @max-2xl:pb-6 @2xl:px-4",
										isCurrent && "visible",
									)}
								>
									<div
										className={cn(
											"flex h-0 w-xs flex-col items-center gap-2 rounded-2xl border border-mint-20",
											"@2xl:h-80 @2xl:w-full @2xl:min-w-full @2xl:p-6",
											isCurrent && "h-80 border-green-60 bg-mint-0 p-6",
										)}
									>
										<h4 className="text-center text-navy-60">
											Step {index + 1} of {steps.length}
										</h4>

										{step.content}
									</div>
								</div>
							</div>
						</li>
					)
				})}

				<div className="hidden min-w-lg @2xl:block" />
			</ul>

			<div className="hidden @2xl:block">
				<Tooltip title={<ShortcutLabel label="Next" shortcut="→" />}>
					<Button
						type="text"
						disabled={current === steps.length - 1 || steps.length === 0}
						icon={<RightOutlined aria-hidden />}
						aria-label={`Go to ${nextStep ? `step ${nextStep.title ?? nextStep.subtitle}` : "next step"}`}
						onClick={handleNext}
					/>
				</Tooltip>
			</div>
		</div>
	)
}

function stepId(index: number) {
	return `step-${index}`
}
